import React, { useMemo } from 'react';
import { MUSIC_LIBRARY } from '@airhead-games/shared/configs';
import type { RichTextNode, Widget } from '@airhead-games/shared/content';
import SortableTable from '@/content/rendering/renderers/blocks/SortableTable';

type SongEntry = {
  id: string;
  title: string;
  artist: string;
  album: string;
  styles: string;
};

interface WidgetSongTableProps {
  widget: Extract<Widget, { type: 'songTable' }>;
}

const textCell = (value: string): RichTextNode[] => [
  { type: 'text', text: value },
];

const WidgetSongTable: React.FC<WidgetSongTableProps> = ({ widget }) => {
  const songs = useMemo<SongEntry[]>(
    () =>
      MUSIC_LIBRARY.artists.flatMap((artist) =>
        artist.albums.flatMap((album) =>
          album.songs
            .filter((song) => song.game === widget.gameId)
            .map((song) => ({
              id: song.id,
              title: song.title,
              artist: artist.name,
              album: album.name,
              styles: song.tags?.join(', ') || 'n/a',
            }))
        )
      ),
    [widget.gameId]
  );

  if (songs.length === 0) {
    return (
      <p className="text-sm leading-6 text-muted-foreground">
        No songs available yet.
      </p>
    );
  }

  const rows: RichTextNode[][][] = songs.map((song) => [
    [
      {
        type: 'link',
        href: `/discover/music?song=${encodeURIComponent(song.id)}`,
        children: textCell(song.title),
      },
    ],
    textCell(song.artist),
    textCell(song.album),
    textCell(song.styles),
  ]);

  return (
    <SortableTable
      columns={[
        textCell('Song name'),
        textCell('Artist'),
        textCell('Album'),
        textCell('Styles'),
      ]}
      rows={rows}
    />
  );
};

export default WidgetSongTable;
